import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import type { ConfigType } from '@plone/registry';
import downSVG from '@plone/volto/icons/down-key.svg';
import rightSVG from '@plone/volto/icons/right-key.svg';
// Schema de settings do addon (title_size, right_arrows, collapsed, ...).
// Import direto pelo nome do addon (o alias do webpack já aponta para o src;
// usar `/src/` duplicaria o caminho). O resolver do ESLint usa resolução Node
// e não conhece esse alias, por isso o no-unresolved é desativado aqui.
// eslint-disable-next-line import/no-unresolved
import { AccordionBlockSchema } from '@eeacms/volto-accordion-block/components/manage/Blocks/Accordion/Schema';

import AccordionNoHydrationView from 'volto-ministerio-publico-federal/components/Blocks/Accordion/View';
import withAccordionColor from 'volto-ministerio-publico-federal/components/Blocks/Accordion/withAccordionColor';
import {
  DEFAULT_ACCORDION_GAP,
  DEFAULT_ACCORDION_THEME,
} from 'volto-ministerio-publico-federal/components/Blocks/Accordion/constants';

const ACCORDION_BLOCK = 'accordion';

const messages = defineMessages({
  blue: { id: 'accordion_color_blue', defaultMessage: 'Azul' },
  green: { id: 'accordion_color_green', defaultMessage: 'Verde' },
  darkBlue: { id: 'accordion_color_dark_blue', defaultMessage: 'Azul Escuro' },
  lightBlue: { id: 'accordion_color_light_blue', defaultMessage: 'Azul Claro' },
  gapLabel: {
    id: 'accordion_gap_label',
    defaultMessage: 'Espaçamento entre painéis',
  },
  gapXs: { id: 'accordion_gap_xs', defaultMessage: 'XS — 4px' },
  gapSm: { id: 'accordion_gap_sm', defaultMessage: 'SM — 8px' },
  gapMd: { id: 'accordion_gap_md', defaultMessage: 'MD — 16px' },
  gapLg: { id: 'accordion_gap_lg', defaultMessage: 'LG — 24px' },
});

/** Seta do título: fechado aponta para a direita, aberto para baixo. */
const applyTitleIcons = (accordion: any) => {
  accordion.titleIcons = {
    ...accordion.titleIcons,
    closed: { leftPosition: rightSVG, rightPosition: rightSVG },
    opened: { leftPosition: downSVG, rightPosition: downSVG },
  };
};

/**
 * O addon registra só `schema` (layout dos painéis), nunca `blockSchema`,
 * então o `applyBlockDefaults` do Volto não aplica os defaults dos campos de
 * settings. Apontando o `blockSchema` para o schema de settings do addon, os
 * defaults (`right_arrows: true` => seta à direita, `collapsed: true`,
 * `non_exclusive: true`) passam a valer em todo bloco — editor e view, novos e
 * existentes. Sem isso, a seta caía à esquerda quando o campo estava ausente.
 */
const applyDefaults = (accordion: any) => {
  accordion.defaults = {
    ...accordion.defaults,
    theme: DEFAULT_ACCORDION_THEME,
  };
  accordion.blockSchema = AccordionBlockSchema;
};

/** Popula o Theme picker com as quatro cores institucionais. */
const addThemeColors = (schema: any, intl: IntlShape) => {
  const themeField = schema?.properties?.styles?.schema?.properties?.theme;
  if (!themeField) return;

  themeField.colors = [
    { name: 'blue', label: intl.formatMessage(messages.blue) },
    { name: 'green', label: intl.formatMessage(messages.green) },
    { name: 'dark-blue', label: intl.formatMessage(messages.darkBlue) },
    { name: 'light-blue', label: intl.formatMessage(messages.lightBlue) },
  ];
  themeField.default = DEFAULT_ACCORDION_THEME;
};

/** Espaçamento entre painéis, na mesma seção ESTILO do Theme picker. */
const addGapField = (schema: any, intl: IntlShape) => {
  const stylesSchema = schema?.properties?.styles?.schema;
  if (!stylesSchema?.properties || stylesSchema.properties.gap) return;

  stylesSchema.properties.gap = {
    title: intl.formatMessage(messages.gapLabel),
    choices: [
      ['xs', intl.formatMessage(messages.gapXs)],
      ['sm', intl.formatMessage(messages.gapSm)],
      ['md', intl.formatMessage(messages.gapMd)],
      ['lg', intl.formatMessage(messages.gapLg)],
    ],
    default: DEFAULT_ACCORDION_GAP,
    noValueOption: false,
  };

  const fieldset = stylesSchema.fieldsets?.[0];
  if (fieldset && !fieldset.fields.includes('gap')) {
    fieldset.fields = [...fieldset.fields, 'gap'];
  }
};

/** Campo "color" e fieldset "Cor" de antes do Theme picker. */
const removeLegacyColorField = (schema: any) => {
  if (!schema?.properties?.color) return;

  delete schema.properties.color;
  schema.fieldsets = (schema.fieldsets || []).filter(
    (fieldset: { id?: string }) => fieldset.id !== 'mpf_color',
  );
};

const applySchemaEnhancer = (accordion: any) => {
  const base = accordion.schemaEnhancer;

  accordion.schemaEnhancer = (props: any) => {
    const schema = base ? base(props) : props.schema;
    addThemeColors(schema, props.intl);
    addGapField(schema, props.intl);
    removeLegacyColorField(schema);
    return schema;
  };
};

/** Publicado: view nativa, sem hidratação. Editor: o `edit` do addon. */
const applyWrappers = (accordion: any) => {
  accordion.view = withAccordionColor(AccordionNoHydrationView);
  accordion.edit = withAccordionColor(accordion.edit);
};

/**
 * Estende o bloco accordion do `@eeacms/volto-accordion-block`.
 *
 * A página publicada usa uma view sem hidratação (`<details>` nativo, ver
 * `components/Blocks/Accordion/View.tsx`) no lugar da view React +
 * `animate-height` do addon; o editor continua com o `edit` do addon, de UI de
 * gerenciamento de painéis intacta.
 *
 * De baixo acoplamento de propósito: não copiamos (shadow) os componentes do
 * addon, o que evitaria drift a cada atualização dele. Como esses campos
 * (`titleIcons`, `defaults`, `blockSchema`) não fazem parte do tipo de bloco do
 * `ConfigType`, este ponto de integração trata a config do bloco como `any`.
 */
export const updateAccordionConfig = (config: ConfigType) => {
  const accordion: any = (config.blocks.blocksConfig as any)[ACCORDION_BLOCK];
  // O addon registra o bloco antes deste update rodar; se não estiver
  // instalado, não há o que estender.
  if (!accordion) return config;

  applyTitleIcons(accordion);
  applyDefaults(accordion);
  applySchemaEnhancer(accordion);
  applyWrappers(accordion);

  return config;
};

export default updateAccordionConfig;
